import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Minus } from "lucide-react";
import { servicesList as staticServices } from "@/data/services";
import api from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import useSEO from "@/lib/useSEO";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const COMPARE_SLUGS = ["consulenza-essenza", "consulenza-atelier", "consulenza-maison"];
const COMPARE_FEATURES = [
  { label: "Analisi delle esigenze e studio degli spazi", values: [true, true, true] },
  { label: "Due proposte di layout e moodboard", values: [true, true, true] },
  { label: "Viste volumetriche 3D di studio", values: [true, true, true] },
  { label: "Una revisione e book digitale", values: [true, true, true] },
  { label: "Render 3D fotorealistici", values: [false, true, true] },
  { label: "Progetto dell'illuminazione", values: [false, true, true] },
  { label: "Arredi su misura ed elaborati tecnici", values: [false, true, true] },
  { label: "Shopping list con fornitori", values: [false, true, true] },
  { label: "Affiancamento nella scelta di materiali e finiture", values: [false, false, true] },
  { label: "Appuntamenti concordati in showroom e fornitori", values: [false, false, true] },
  { label: "Confronto estetico con fornitori e artigiani", values: [false, false, true] },
  { label: "Direzione artistica durante la realizzazione", values: [false, false, true] },
  { label: "Verifica della coerenza estetica del progetto", values: [false, false, true] },
];

export default function Servizi() {
  useSEO({ title: "Servizi", description: "Consulenza Essenza, Atelier e Maison: percorsi di interior design con affiancamento e direzione artistica, a Napoli e online." });
  const navigate = useNavigate();
  const [list, setList] = useState(staticServices);
  const [est, setEst] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => { if (r.data?.length) setList(r.data); }).catch(() => {});
    api.get("/estimator/config").then((r) => { if (r.data?.available) setEst(r.data); }).catch(() => {});
  }, []);

  const startingPrice = (p) => p.price_from ?? null;

  return (
    <div data-testid="page-servizi">
      <section className="container-x pt-10 pb-16">
        <span className="eyebrow">Servizi</span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-5xl">
          Un percorso su misura <br />
          <em className="text-gold font-light">per ogni tua esigenza.</em>
        </h1>
        <p className="mt-8 text-taupe max-w-2xl text-lg leading-relaxed font-light">
          Crediamo che ogni casa abbia un'anima e ogni cliente necessità uniche.
          Per questo non offriamo soluzioni standard, ma tre percorsi progressivi —
          più una consulenza per il singolo ambiente — per affiancarti nel modo più corretto:
          dalla prima idea di spazio fino alla direzione artistica della realizzazione.
        </p>
      </section>

      <section className="container-x pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          {list.map((s, idx) => (
            <Reveal key={s.slug} delay={idx * 0.1} className={idx % 2 === 1 ? "md:mt-10" : ""}>
            <div
              role="link"
              tabIndex={0}
              onClick={() => { navigate(`/servizi/${s.slug}`); window.scrollTo(0, 0); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { navigate(`/servizi/${s.slug}`); window.scrollTo(0, 0); } }}
              data-testid={`service-card-${s.slug}`}
              className="group relative bg-white border border-hair p-10 md:p-12 hover:border-gold transition-all duration-500 flex flex-col cursor-pointer select-none h-full"
            >
              <div className="flex justify-between items-start">
                <div className="font-serif text-4xl text-gold">{s.number}</div>
                <ArrowUpRight
                  size={22}
                  className="text-ink/30 group-hover:text-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                />
              </div>
              <div className="mt-16 md:mt-24">
                <div className="eyebrow text-taupe">{s.tagline}</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">{s.title}</h2>
                <p className="mt-4 text-taupe leading-relaxed text-sm md:text-base font-light">
                  {s.subtitle}
                </p>
              </div>
              <div className="mt-10 pt-6 border-t border-hair">
                <span className="text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">
                  Scopri di più →
                </span>
              </div>
            </div>
            </Reveal>
          ))}
        </div>
      </section>

      {(() => {
        const cmp = COMPARE_SLUGS.map((sl) => list.find((s) => s.slug === sl)).filter(Boolean);
        if (cmp.length !== 3) return null;
        const priceOf = (slug) => {
          const p = (est?.packages || []).find((x) => x.service_slug === slug);
          return p ? (p.price_from ?? null) : null;
        };
        const Mark = ({ on }) => on
          ? <Check size={16} className="text-gold mx-auto" aria-label="Incluso" />
          : <Minus size={16} className="text-taupe/50 mx-auto" aria-label="Non incluso" />;
        return (
          <section className="container-x pb-24" data-testid="servizi-compare">
            <Reveal>
              <span className="eyebrow text-gold">Confronta i percorsi</span>
              <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight max-w-3xl">
                Quale percorso <em className="text-gold font-light">fa per te?</em>
              </h2>
            </Reveal>

            {/* Desktop: tabella */}
            <Reveal delay={0.1} className="hidden md:block">
              <table className="w-full mt-10 bg-white border border-hair" data-testid="servizi-compare-table">
                <thead>
                  <tr className="border-b border-hair">
                    <th className="text-left p-5 w-[32%]" />
                    {cmp.map((s, i) => (
                      <th key={s.slug} className={`p-5 text-center align-top ${i === 1 ? "bg-bone/60 border-x border-hair" : ""}`} data-testid={`compare-head-${s.slug}`}>
                        <span className="eyebrow text-taupe block">{s.tagline}</span>
                        <span className="font-serif text-2xl block mt-2">{s.title}</span>
                        {priceOf(s.slug) != null && (
                          <span className="font-serif text-lg text-gold block mt-2">da {eur(priceOf(s.slug))}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-hair">
                    <td className="p-5 text-[10px] tracking-[0.14em] uppercase text-taupe">Ideale per</td>
                    {cmp.map((s, i) => (
                      <td key={s.slug} className={`p-5 text-sm text-center text-taupe ${i === 1 ? "bg-bone/60 border-x border-hair" : ""}`}>{s.audience_label}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-hair">
                    <td className="p-5 text-[10px] tracking-[0.14em] uppercase text-taupe">Tempi indicativi</td>
                    {cmp.map((s, i) => (
                      <td key={s.slug} className={`p-5 text-sm text-center ${i === 1 ? "bg-bone/60 border-x border-hair" : ""}`}>{s.timing}</td>
                    ))}
                  </tr>
                  {COMPARE_FEATURES.map((f) => (
                    <tr key={f.label} className="border-b border-hair last:border-0" data-testid={`compare-row-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="p-5 text-sm">{f.label}</td>
                      {f.values.map((v, i) => (
                        <td key={i} className={`p-5 text-center ${i === 1 ? "bg-bone/60 border-x border-hair" : ""}`}><Mark on={v} /></td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-5" />
                    {cmp.map((s, i) => (
                      <td key={s.slug} className={`p-5 text-center ${i === 1 ? "bg-bone/60 border-x border-hair" : ""}`}>
                        <Link to={`/servizi/${s.slug}`} className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink hover:text-gold transition-colors" data-testid={`compare-cta-${s.slug}`}>
                          Scopri <ArrowUpRight size={14} />
                        </Link>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </Reveal>

            {/* Mobile: schede */}
            <div className="md:hidden mt-10 space-y-4">
              {cmp.map((s, si) => (
                <Reveal key={s.slug} delay={si * 0.08}>
                  <div className="bg-white border border-hair p-6" data-testid={`compare-card-${s.slug}`}>
                    <span className="eyebrow text-taupe">{s.tagline}</span>
                    <h3 className="font-serif text-2xl mt-2">{s.title}</h3>
                    {priceOf(s.slug) != null && <p className="font-serif text-lg text-gold mt-1">da {eur(priceOf(s.slug))}</p>}
                    <p className="text-sm text-taupe mt-3">{s.audience_label}</p>
                    <p className="text-xs text-taupe mt-1">{s.timing}</p>
                    <ul className="mt-4 pt-4 border-t border-hair space-y-2.5">
                      {COMPARE_FEATURES.map((f) => (
                        <li key={f.label} className="flex items-center gap-3 text-sm">
                          {f.values[si]
                            ? <Check size={15} className="text-gold shrink-0" />
                            : <Minus size={15} className="text-taupe/50 shrink-0" />}
                          <span className={f.values[si] ? "" : "text-taupe/60"}>{f.label}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={`/servizi/${s.slug}`} className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink hover:text-gold transition-colors" data-testid={`compare-card-cta-${s.slug}`}>
                      Scopri {s.title} <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        );
      })()}

      {est && (
        <section className="container-x pb-24" data-testid="servizi-estimator-showcase">
          <Reveal>
            <div className="relative bg-white border border-gold/40 p-8 md:p-14 overflow-hidden">
              <span className="absolute top-0 left-0 w-full h-[3px] bg-gold" aria-hidden="true" />
              <div className="flex flex-col lg:flex-row justify-between gap-10">
                <div className="max-w-xl">
                  <span className="eyebrow text-gold">Calcola la tua stima gratuita</span>
                  <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
                    Quanto costa <em className="text-gold font-light">il tuo progetto?</em>
                  </h2>
                  <p className="mt-5 text-taupe leading-relaxed font-light">
                    Rispondi a poche domande e ricevi subito una stima preliminare personalizzata in PDF,
                    elaborata sulle tariffe reali dello Studio. Gratuita, senza impegno, senza attese.
                  </p>
                  <Link
                    to="/stima"
                    className="mt-8 inline-flex items-center gap-3 px-8 py-3 min-h-[48px] text-sm tracking-[0.14em] uppercase bg-ink text-bone hover:bg-gold hover:text-ink transition-colors"
                    data-testid="servizi-cta-stima"
                  >
                    Calcola la tua stima gratuita <ArrowRight size={16} />
                  </Link>
                  <p className="mt-3 text-xs text-taupe">Circa 2 minuti · PDF immediato · nessun pagamento richiesto</p>
                </div>
                <div className="grid grid-cols-2 gap-px bg-hair border border-hair self-start w-full lg:w-auto">
                  {(est.packages || []).map((p) => {
                    const price = startingPrice(p);
                    return (
                      <div key={p.key} className="bg-white p-5 md:p-6 min-w-[150px]" data-testid={`servizi-est-${p.key}`}>
                        <p className="text-[10px] tracking-[0.14em] uppercase text-taupe">{p.name}</p>
                        <p className="font-serif text-xl md:text-2xl mt-2">
                          {price != null ? <>da {eur(price)}</> : "Su misura"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <section className="bg-ink text-bone py-20">
        <div className="container-x flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div>
            <span className="eyebrow text-gold">Iniziamo</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-bone">
              Non sai quale percorso scegliere?
            </h2>
            <p className="mt-3 text-bone/70 max-w-xl font-light">
              Prenota una chiamata conoscitiva gratuita di 15 minuti. Capiremo
              insieme qual è il percorso più adatto a te.
            </p>
            <p className="mt-4 text-bone/50 max-w-xl text-xs leading-relaxed">
              I clienti dello Studio accedono a condizioni esclusive sui prodotti Shop selezionati con Shop the Project.
            </p>
            <Link to="/shop-the-project" className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-gold hover:text-bone transition-colors" data-testid="servizi-stp-card">
              Shop the Project: la selezione d'acquisto curata dallo Studio <ArrowUpRight size={13} />
            </Link>
          </div>
          <Link
            to="/prenota"
            className="inline-flex items-center gap-3 px-8 py-3 text-sm tracking-[0.14em] uppercase border border-bone text-bone hover:bg-bone hover:text-ink transition-all shrink-0"
            data-testid="servizi-cta-prenota"
          >
            Prenota Consulenza
          </Link>
        </div>
      </section>
    </div>
  );
}
